import type { PaazlDeliveryOptionsRequestObject } from '@/types/Paazl/PaazlDeliveryOptionsRequestObject.ts';
import { useSizeStore } from '@/stores/Configurator/SizeStore.ts';
import _ from 'lodash';
import {
  ConfigurableProductAttributeObject,
  ConfigurableProductSizeValueObject,
} from '@/types/joostconnector';
import { MediaclipProductType } from '@/types/Mediaclip/MediaclipProductType.ts';

export function usePaazl() {
  function getRequestData(
    getLowestProduct: boolean = false
  ): PaazlDeliveryOptionsRequestObject {
    return getConfiguratorProductRequestData(getLowestProduct);
  }

  const isSimpleProduct =
    window.product.mediaclip_product_type == MediaclipProductType.NONE;

  function getConfiguratorProductRequestData(
    getLowestProduct: boolean = false
  ): PaazlDeliveryOptionsRequestObject {
    // Check if the product is a simple product
    if (isSimpleProduct) {
      const product = window.simple_product!.connected_product;
      return getRequestObject(
        product.sku,
        Number(product.width ?? 0),
        Number(product.height ?? 0),
        Number(product.price ?? 0),
        Number(product.processing_days ?? 0),
        product.shipping_cutoff_time ?? undefined,
        product.paazl_delivery_matrix ?? undefined
      );
    }

    const sizeStore = useSizeStore();
    const selectedSizeValue = sizeStore.selectedSizeValue;

    if (!selectedSizeValue) {
      throw new Error('No selected size value found.');
    }

    const productAttributes = getLowestProduct
      ? (getCheapestProduct(sizeStore.allAvailableSizeValues) ??
        selectedSizeValue.attributes)
      : selectedSizeValue.attributes;

    return getRequestObject(
      productAttributes.sku,
      Number(productAttributes.width ?? 0),
      Number(productAttributes.height ?? 0),
      Number(productAttributes.price ?? 0),
      Number(productAttributes.processingDays ?? 0),
      productAttributes.shippingCutoffTime ?? undefined,
      productAttributes.paazlDeliveryMatrix ?? undefined
    );
  }

  function getRequestObject(
    sku: string,
    width: number,
    height: number,
    productPrice: number,
    processingDays: number,
    shippingCutoffTime?: string,
    paazlDeliveryMatrix?: string
  ): PaazlDeliveryOptionsRequestObject {
    return {
      sku: sku,
      shipping_cutoff_time: shippingCutoffTime,
      processing_days: processingDays,
      width: width,
      height: height,
      paazl_delivery_matrix: paazlDeliveryMatrix,
      product_price: productPrice,
    };
  }

  function getCheapestProduct(
    products: Record<string, ConfigurableProductSizeValueObject>
  ): ConfigurableProductAttributeObject | undefined {
    return _.minBy(
      _.map(products, value => value?.attributes),
      attribute => attribute?.price
    );
  }

  return {
    getRequestData,
  };
}
